import { Fragment, useMemo, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import {
  Button,
  EmptyState,
  InfoBox,
  NativeSelect,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  cn,
} from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import {
  FACT_KEY_GROUPS,
  FACT_KEY_ORIGINS,
  FACT_KEY_STATUSES,
  PROPERTY_CATEGORIES,
  type FactKeyView,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useCan } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { EuroIcon, PlusIcon, ShieldIcon, TagIcon, TextIcon } from '@/components/layout/icons';
import { FilterBar } from '@/components/filter-bar';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { readEnumParam, SEARCH_PARAM, useListParams } from '@/lib/list-view/use-list-params';
import { CreateFactKeyDialog } from '@/pages/skills/create-fact-key-dialog';
import { FactKeyEntryModal } from '@/pages/skills/fact-key-entry-modal';
import {
  matchesNeedFilter,
  NEED_FILTER_VALUES,
  needSortValue,
  strongestNeedByProduct,
} from '@/pages/skills/fact-key-needs';

export const factKeysLoaderParams = [] as const;

export type FactKeysLoaderData = {
  readonly factKeys: ReadonlyArray<FactKeyView>;
  readonly columnPreference: ColumnPreference;
};

export async function factKeysLoader(): Promise<FactKeysLoaderData> {
  const [result, columnPreference] = await Promise.all([
    apiClient.adminFactKeys.list(),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.factKeys),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load fact keys', { status: result.status });
  }

  return { factKeys: result.body.data, columnPreference };
}

const DEFAULT_HIDDEN_IDS = ['frequency', 'scope', 'publicSafe', 'propertyField', 'parent'] as const;

const idCell = (key: string) =>
  key.split('.').map((part, index, parts) => (
    <Fragment key={part + index}>
      {part}
      {index < parts.length - 1 && '.'}
      {index < parts.length - 1 && <wbr />}
    </Fragment>
  ));

function useFactKeyColumns(onOpen: (key: string) => void): ReadonlyArray<OperatorColumn<FactKeyView>> {
  const t = useTranslations('pages.admin.skills.fact-keys');
  const tCategory = useTranslations('common.immobilientyp.category');

  return useMemo(() => {
    const categoriesOf = (row: FactKeyView) =>
      PROPERTY_CATEGORIES.filter((category) => row.categories.includes(category))
        .map((category) => tCategory(category))
        .join(', ');

    return [
      {
        id: 'key',
        label: t('columns.key'),
        icon: TagIcon,
        headClassName: 'w-[215px]',
        cellClassName: 'font-mono text-xs text-text-secondary',
        cell: (row) => (
          <button type="button" className="text-left font-mono text-xs" onClick={() => onOpen(row.key)}>
            {idCell(row.key)}
          </button>
        ),
        sortValue: (row) => row.key,
      },
      {
        id: 'label',
        label: t('columns.label'),
        icon: TextIcon,
        headClassName: 'w-[150px]',
        cell: (row) => row.label,
        sortValue: (row) => row.label,
      },
      {
        id: 'group',
        label: t('columns.group'),
        icon: TextIcon,
        headClassName: 'w-[100px]',
        cell: (row) => t(`group.${row.groupId}`),
        sortValue: (row) => t(`group.${row.groupId}`),
      },
      {
        id: 'valueType',
        label: t('columns.value-type'),
        icon: TextIcon,
        headClassName: 'w-[80px]',
        cellClassName: 'text-text-tertiary',
        cell: (row) => t(`value-type.${row.valueType}`),
        sortValue: (row) => t(`value-type.${row.valueType}`),
      },
      {
        id: 'frequency',
        label: t('columns.frequency'),
        icon: TextIcon,
        cell: (row) => t(`frequency.${row.frequency}`),
        sortValue: (row) => t(`frequency.${row.frequency}`),
      },
      {
        id: 'categories',
        label: t('columns.categories'),
        icon: TextIcon,
        headClassName: 'w-[110px]',
        cell: categoriesOf,
        sortValue: categoriesOf,
      },
      {
        id: 'needs',
        label: t('columns.needs'),
        icon: TextIcon,
        headClassName: 'w-[175px]',
        cell: (row) => {
          const strongest = strongestNeedByProduct(row.needs);

          if (strongest.length === 0) return <span className="text-text-tertiary">{t('need.NOT_USED')}</span>;

          return (
            <div className="space-y-0.5">
              {strongest.map(({ product, need }) => (
                <div key={product}>
                  <span className={need === 'REQUIRED' ? 'font-semibold' : need === 'RECOMMENDED' ? 'font-medium' : ''}>
                    {t(`need.${need}`)}
                  </span>
                  <span className="text-text-tertiary">{` · ${t(`product.${product}`)}`}</span>
                </div>
              ))}
            </div>
          );
        },
        sortValue: (row) => needSortValue(row.needs),
      },
      {
        id: 'origin',
        label: t('columns.origin'),
        icon: TextIcon,
        headClassName: 'w-[105px]',
        cell: (row) => t(`origin.${row.origin}`),
        sortValue: (row) => t(`origin.${row.origin}`),
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TextIcon,
        headClassName: 'w-[100px]',
        cell: (row) => (
          <StatusBadge
            size="sm"
            dot
            tone={row.status === 'ACTIVE' ? 'success' : 'neutral'}
            label={t(`status.${row.status}`)}
          />
        ),
        sortValue: (row) => t(`status.${row.status}`),
      },
      {
        id: 'scope',
        label: t('columns.scope'),
        icon: EuroIcon,
        cell: (row) => t(`scope.${row.scope}`),
        sortValue: (row) => t(`scope.${row.scope}`),
      },
      {
        id: 'publicSafe',
        label: t('columns.public-safe'),
        icon: ShieldIcon,
        cell: (row) => t(`yes-no.${String(row.publicSafe)}`),
        sortValue: (row) => t(`yes-no.${String(row.publicSafe)}`),
      },
      {
        id: 'propertyField',
        label: t('columns.property-field'),
        icon: TextIcon,
        cell: (row) => row.propertyFieldKey ?? '',
        sortValue: (row) => row.propertyFieldKey,
      },
      {
        id: 'parent',
        label: t('columns.parent'),
        icon: TextIcon,
        cellClassName: 'font-mono text-xs',
        cell: (row) => row.parentKey ?? '',
        sortValue: (row) => row.parentKey,
      },
    ];
  }, [t, tCategory, onOpen]);
}

type FilterSelectProps = {
  readonly label: string;
  readonly value: string;
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  readonly onChange: (value: string) => void;
};

function FilterSelect({ label, value, options, onChange }: FilterSelectProps) {
  const t = useTranslations('pages.admin.skills.fact-keys');

  return (
    <label className="flex flex-col gap-1 text-sm text-text-tertiary">
      {label}
      <NativeSelect
        size="sm"
        aria-label={label}
        value={value}
        options={[{ value: '', label: t('filters.all') }, ...options]}
        onChange={onChange}
      />
    </label>
  );
}

export function FactKeysPage() {
  const t = useTranslations('pages.admin.skills.fact-keys');
  const { factKeys, columnPreference } = useLoaderData() as FactKeysLoaderData;
  const { revalidate } = useRevalidator();
  const canManage = useCan('backoffice:product-skill:manage');

  const { params, setParams } = useListParams();
  const query = params.get(SEARCH_PARAM) ?? '';
  const group = readEnumParam(params, 'group', FACT_KEY_GROUPS);
  const need = readEnumParam(params, 'need', NEED_FILTER_VALUES);
  const origin = readEnumParam(params, 'origin', FACT_KEY_ORIGINS);
  const status = readEnumParam(params, 'status', FACT_KEY_STATUSES);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return factKeys.filter(
      (row) =>
        (needle === '' || row.key.toLowerCase().includes(needle) || row.label.toLowerCase().includes(needle)) &&
        (group === null || row.groupId === group) &&
        (need === null || matchesNeedFilter(row.needs, need)) &&
        (origin === null || row.origin === origin) &&
        (status === null || row.status === status),
    );
  }, [factKeys, query, group, need, origin, status]);

  const catalogue = useFactKeyColumns(setOpenKey);
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.factKeys,
    catalogue,
    alwaysVisibleId: 'key',
    defaultHiddenIds: DEFAULT_HIDDEN_IDS,
    loadedPreference: columnPreference,
  });
  const openRow = openKey === null ? undefined : factKeys.find((row) => row.key === openKey);

  return (
    <>
      <PageTopbar
        leading={<ListLeading title={t('title')} count={filtered.length} icon={TagIcon} />}
        trailing={
          canManage ? (
            <Button size="sm" onClick={() => setCreating(true)}>
              <PlusIcon className="h-4 w-4" />
              {t('create.button')}
            </Button>
          ) : undefined
        }
      />
      <FilterBar
        searchValue={query}
        searchPlaceholder={t('search')}
        onSearchChange={(next, commit) => setParams({ [SEARCH_PARAM]: next }, { replace: commit.continuing })}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      <div className="flex flex-wrap items-end gap-3 border-b border-border bg-surface px-4 py-2">
        <FilterSelect
          label={t('filters.group')}
          value={group ?? ''}
          options={FACT_KEY_GROUPS.map((token) => ({ value: token, label: t(`group.${token}`) }))}
          onChange={(next) => setParams({ group: next || null })}
        />
        <FilterSelect
          label={t('filters.need')}
          value={need ?? ''}
          options={NEED_FILTER_VALUES.map((value) => {
            const [product, needToken] = value.split('.');

            return {
              value,
              label: t('need-filter-option', { need: t(`need.${needToken}`), product: t(`product.${product}`) }),
            };
          })}
          onChange={(next) => setParams({ need: next || null })}
        />
        <FilterSelect
          label={t('filters.origin')}
          value={origin ?? ''}
          options={FACT_KEY_ORIGINS.map((token) => ({ value: token, label: t(`origin.${token}`) }))}
          onChange={(next) => setParams({ origin: next || null })}
        />
        <FilterSelect
          label={t('filters.status')}
          value={status ?? ''}
          options={FACT_KEY_STATUSES.map((token) => ({ value: token, label: t(`status.${token}`) }))}
          onChange={(next) => setParams({ status: next || null })}
        />
      </div>

      <div className="space-y-3 p-4">
        {!canManage && <InfoBox type="info">{t('read-only-hint')}</InfoBox>}
        <InfoBox type="warning">{t('public-safe-warning')}</InfoBox>
      </div>

      {filtered.length === 0 ? (
        <div className="p-6">
          <EmptyState title={t('empty')} />
        </div>
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow {...table.columns.headerRowProps}>
              {table.columns.visibleColumns.map((column) => (
                <OperatorColumnHead key={column.id} column={column} table={table} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.sorted.map((row) => (
              <TableRow key={row.key} interactive selected={openKey === row.key} onClick={() => setOpenKey(row.key)}>
                {table.columns.visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                    style={column.cellStyle}
                  >
                    {column.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {creating && (
        <CreateFactKeyDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            revalidate();
          }}
        />
      )}

      {openRow !== undefined && (
        <FactKeyEntryModal
          row={openRow}
          canManage={canManage}
          onClose={() => setOpenKey(null)}
          onSaved={() => {
            setOpenKey(null);
            revalidate();
          }}
          onRetired={revalidate}
        />
      )}
    </>
  );
}
